import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  Metric,
  formatDate,
} from "@odingard/design-system";
import { Link } from "react-router-dom";
import type { FrameworkCoverageMetric } from "@odingard/sdk-client";
import { ApiError } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { governanceApi } from "../../lib/api";
import { PolicyDemoBanner, PolicyLiveBadge } from "../../components/PolicyMode";

type CoverageRow = FrameworkCoverageMetric & { id: string };

const PERCENT = (ratio: number) => `${Math.round(ratio * 100)}%`;

// APP-004 R3 governance overview. Every value shown here is measured by the
// Policy AI backend — readiness comes from frozen snapshots and is never
// recomputed in the browser.
export function GovOverview() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const state = useAsync(async () => governanceApi.executiveDashboard(orgId), [orgId]);

  const exec = state.data?.data.executiveDashboard;
  const demoMode = state.data?.demoMode ?? false;
  const unavailable = state.error?.code === "service_unavailable";

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Governance &amp; Compliance</h1>
          <p>Framework coverage, evidence health, findings and audit readiness across this organization.</p>
        </div>
      </div>

      <PolicyDemoBanner show={demoMode} />
      <div style={{ marginBottom: "var(--ods-space-3)" }}>
        <PolicyLiveBadge demoMode={demoMode} />
      </div>

      {state.loading && <LoadingState label="Loading governance overview…" />}
      {unavailable && (
        <ErrorState
          title="Governance is temporarily unavailable"
          description="The Policy AI service is not reachable right now. No governance data is being shown."
          onRetry={state.reload}
        />
      )}
      {state.error && !unavailable && (
        <ErrorState description={describe(state.error)} onRetry={state.reload} />
      )}

      {exec && (
        <>
          <div className="metric-grid">
            <div className="metric-card">
              <Metric value={PERCENT(exec.headline.policyCoverageRatio)} label="Policy coverage" />
            </div>
            <div className="metric-card">
              <Metric value={PERCENT(exec.headline.evidenceFreshnessRatio)} label="Evidence freshness" />
            </div>
            <div className="metric-card">
              <Metric
                value={exec.headline.averageReadinessScore ?? "Not scored"}
                label="Average readiness score"
              />
            </div>
            <div className="metric-card">
              <Metric
                value={exec.headline.verificationBacklog}
                label="Awaiting human verification"
                tone="var(--ods-color-warning)"
              />
            </div>
          </div>

          <Card>
            <CardHeader title="Needs attention" />
            <CardBody>
              {exec.attention.length === 0 ? (
                <EmptyState title="Nothing outstanding" description="No governance work is overdue or awaiting a decision." />
              ) : (
                <ul style={{ margin: 0, paddingLeft: "var(--ods-space-4)" }}>
                  {exec.attention.map((item) => (
                    <li key={item.key}>
                      <Badge tone={item.severity === "critical" ? "danger" : item.severity === "warning" ? "warning" : "neutral"}>
                        {item.severity}
                      </Badge>{" "}
                      {item.label}: <strong>{item.count}</strong>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Framework coverage" />
            <CardBody>
              {exec.dashboard.frameworkCoverage.length === 0 ? (
                <EmptyState
                  title="No frameworks adopted"
                  description="Adopt a framework version to start measuring requirement coverage."
                />
              ) : (
                <DataTable
                  columns={[
                    {
                      key: "frameworkKey",
                      header: "Framework",
                      render: (f: CoverageRow) => `${f.frameworkKey} ${f.versionLabel}`,
                    },
                    { key: "requirementCount", header: "Requirements", render: (f: CoverageRow) => f.requirementCount },
                    { key: "verifiedCount", header: "Verified mappings", render: (f: CoverageRow) => f.verifiedCount },
                    { key: "evidencedCount", header: "Evidenced", render: (f: CoverageRow) => f.evidencedCount },
                    {
                      key: "coverageRatio",
                      header: "Coverage",
                      render: (f: CoverageRow) => PERCENT(f.coverageRatio),
                    },
                  ]}
                  rows={exec.dashboard.frameworkCoverage.map(
                    (f): CoverageRow => ({ ...f, id: f.frameworkVersionId }),
                  )}
                  caption="Framework coverage"
                />
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Take this away" />
            <CardBody>
              <p className="muted-note">
                Every figure above can be exported as a dated, hashed copy.
              </p>
              <p>
                <Link to="/studio/governance/reports">Reports</Link> turns this position into a
                deterministic export. Two runs over an unchanged position carry the same input hash,
                so a figure quoted to an auditor can always be reconciled.
              </p>
            </CardBody>
          </Card>

          <p style={{ marginTop: "var(--ods-space-3)", color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
            Measured {formatDate(exec.generatedAt)}. AI confidence metrics are informational only and never
            affect readiness scoring.
          </p>
        </>
      )}
    </>
  );
}

function describe(error: ApiError): string {
  // A backend permission denial names the missing key so an administrator can
  // grant exactly that permission; never guess or silently hide the action.
  if (error.status === 403) return `${error.message} (${error.code})`;
  return error.message;
}
